import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useCachedGet } from "@/lib/cache";
import { useAuth } from "@/context/AuthContext";
import { fechaSolo } from "@/lib/constants";
import { PeriodoTag } from "@/components/PeriodoTag";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Trello, ChevronLeft, ChevronRight, GripVertical, Search, X, CalendarClock } from "lucide-react";

const COL_TINT = {
  "Pendiente": "border-t-slate-400",
  "En corte": "border-t-amber-400",
  "En pespunte": "border-t-blue-400",
  "En montado": "border-t-violet-400",
  "En adorno": "border-t-pink-400",
  "Terminado": "border-t-emerald-400",
  "Entregado": "border-t-slate-800",
};

export default function MuestrasTablero() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data, setData, refetch: load } = useCachedGet("/muestras/productos");
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);
  const [confirmEntrega, setConfirmEntrega] = useState(null);
  const [busqueda, setBusqueda] = useState("");
  const [verArchivados, setVerArchivados] = useState(false);
  const [busquedaDebounced, setBusquedaDebounced] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setBusquedaDebounced(busqueda), 300);
    return () => clearTimeout(t);
  }, [busqueda]);

  const puedeVer = user?.es_admin || user?.perms?.acciones?.ver_muestras || user?.perms?.acciones?.gestionar_muestras;
  const puedeGestionar = user?.es_admin || user?.perms?.acciones?.gestionar_muestras;
  if (user && !puedeVer) return <Navigate to="/" replace />;

  const setEstado = async (item, estado) => {
    if (item.producto.estado === estado) return;
    // Actualización optimista: mueve la tarjeta al instante y revalida en segundo plano.
    setData((d) => ({ ...d, productos: d.productos.map((p) => p.producto.id === item.producto.id ? { ...p, producto: { ...p.producto, estado } } : p) }));
    try {
      await api.post(`/muestras/${item.muestra_id}/productos/${item.producto.id}/estado`, { estado });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      load();
    }
  };

  const ENTREGADO = data?.estados?.[data.estados.length - 1];

  // Intercepta la transición hacia "Entregado" para pedir confirmación (dispara correo al solicitante).
  const solicitarEstado = (item, estado) => {
    if (item.producto.estado === estado) return;
    if (estado === ENTREGADO) {
      setConfirmEntrega({ item, estado });
      return;
    }
    setEstado(item, estado);
  };

  const mover = (item, dir) => {
    const idx = data.estados.indexOf(item.producto.estado);
    const next = data.estados[idx + dir];
    if (next) solicitarEstado(item, next);
  };

  const onDrop = (est) => {
    setOverCol(null);
    const id = dragId;
    setDragId(null);
    if (!id) return;
    const item = data.productos.find((p) => p.producto.id === id);
    if (!item) return;
    if (item.producto.cancelado) return;
    // No se permite soltar en el carril "Cancelado".
    if (est === "Cancelado") { toast.error("No puedes mover productos al carril Cancelado."); return; }
    // Solo se permite mover a una columna INMEDIATAMENTE adyacente.
    const idxFrom = data.estados.indexOf(item.producto.estado);
    const idxTo = data.estados.indexOf(est);
    if (Math.abs(idxTo - idxFrom) !== 1) {
      toast.error("Debes avanzar de una columna a la vez.");
      return;
    }
    solicitarEstado(item, est);
  };

  if (!data) return <div className="p-6 lg:p-8"><Skeleton className="h-96 rounded-xl" /></div>;
  const q = busquedaDebounced.trim().toLowerCase();
  const productosFiltrados = q ? data.productos.filter((p) => (p.cliente_nombre || "").toLowerCase().includes(q)) : data.productos;
  // Entregados con la recepción confirmada hace más de N días se archivan (botón "Ver archivados").
  const diasArchivo = data.dias_archivo_entregados || 30;
  const esArchivado = (p) => {
    if (p.producto.estado !== ENTREGADO || p.confirmacion_entrega_estado !== "confirmada" || !p.fecha_confirmacion) return false;
    return (Date.now() - new Date(p.fecha_confirmacion).getTime()) / 86400000 > diasArchivo;
  };
  const archivados = productosFiltrados.filter((p) => !p.producto.cancelado && esArchivado(p));
  const pares = (lista) => lista.reduce((s, c) => s + (Number(c.producto.cantidad) || 0), 0);
  // Archivados (si se muestran) van al final de la columna.
  const porEstado = (est) => productosFiltrados
    .filter((p) => !p.producto.cancelado && p.producto.estado === est && (verArchivados || !esArchivado(p)))
    .sort((a, b) => Number(esArchivado(a)) - Number(esArchivado(b)));
  const cancelados = productosFiltrados.filter((p) => p.producto.cancelado);
  const sinResultados = q && productosFiltrados.length === 0;

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-center gap-2 mb-6">
        <div className="h-9 w-9 rounded-xl bg-slate-900 text-white flex items-center justify-center"><Trello size={18} /></div>
        <div>
          <h1 className="font-head text-3xl font-extrabold tracking-tight">Tablero de productos</h1>
          <p className="text-slate-500 text-sm">{puedeGestionar ? "Arrastra las tarjetas o usa las flechas para mover cada producto." : "Estado de cada producto de las muestras autorizadas."}</p>
        </div>
      </div>

      <div className="relative w-full sm:w-72 mb-5">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <Input
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="Buscar por cliente…"
          className="h-9 pl-9 pr-8 bg-white"
          data-testid="buscar-cliente-input"
        />
        {busqueda && (
          <button onClick={() => setBusqueda("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700" data-testid="buscar-cliente-clear" aria-label="Limpiar búsqueda">
            <X size={15} />
          </button>
        )}
      </div>

      {sinResultados ? (
        <Card className="p-12 text-center text-slate-400 border-dashed" data-testid="tablero-sin-resultados">No se encontraron productos para tu búsqueda</Card>
      ) : (
      <div className="flex gap-4 overflow-x-auto thin-scroll pb-4">
        {data.estados.map((est) => {
          const cards = porEstado(est);
          const totalPares = pares(cards);
          const idxCol = data.estados.indexOf(est);
          return (
            <div
              key={est}
              className="w-72 shrink-0"
              data-testid={`col-${est}`}
              onDragOver={(e) => { if (puedeGestionar) { e.preventDefault(); setOverCol(est); } }}
              onDragLeave={() => setOverCol((c) => (c === est ? null : c))}
              onDrop={() => onDrop(est)}
            >
              <div className={`bg-white rounded-t-xl border border-b-0 border-slate-200 border-t-4 ${COL_TINT[est] || "border-t-slate-300"} px-4 py-3 flex items-center justify-between`}>
                <span className="font-semibold text-slate-800 text-sm">{est}</span>
                <span className="text-xs text-slate-400 tabular-nums" data-testid={`col-total-${est}`} title="Pares">{totalPares}</span>
              </div>
              {est === ENTREGADO && archivados.length > 0 && (
                <button
                  onClick={() => setVerArchivados((v) => !v)}
                  className="w-full text-[11px] font-medium text-slate-500 hover:text-slate-800 bg-white border-x border-slate-200 px-4 py-1.5 text-left"
                  data-testid="ver-archivados"
                >
                  {verArchivados ? "Ocultar archivados" : `Ver archivados (${pares(archivados)})`}
                  <span className="block text-[10px] text-slate-400">Recepción confirmada hace más de {diasArchivo} días</span>
                </button>
              )}
              <div className={`rounded-b-xl border border-t-0 border-slate-200 p-3 space-y-3 min-h-[140px] transition-colors ${overCol === est ? "bg-blue-50" : "bg-slate-50"}`}>
                {cards.map((item) => (
                  <Card
                    key={item.producto.id}
                    draggable={puedeGestionar}
                    onDragStart={() => setDragId(item.producto.id)}
                    onDragEnd={() => { setDragId(null); setOverCol(null); }}
                    className={`p-3 shadow-sm border-slate-200 ${puedeGestionar ? "cursor-grab active:cursor-grabbing" : ""} ${dragId === item.producto.id ? "opacity-50" : ""} ${esArchivado(item) ? "opacity-60 bg-slate-50" : ""}`}
                    data-testid={`prod-card-${item.producto.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-[11px] text-slate-400 flex items-center gap-1">{puedeGestionar && <GripVertical size={12} className="text-slate-300" />}{item.folio}</span>
                      {esArchivado(item) && <span className="text-[10px] font-semibold text-slate-500 bg-slate-200 px-1.5 py-0.5 rounded" data-testid={`archivado-${item.producto.id}`}>Archivado</span>}
                    </div>
                    <button onClick={() => navigate(`/muestras/${item.muestra_id}`)} className="text-left w-full" data-testid={`abrir-muestra-${item.producto.id}`}>
                      <div className="font-semibold text-sm text-slate-800 mt-0.5 hover:text-blue-600 transition-colors">{item.producto.nombre}</div>
                      {item.producto.sku && <div className="text-[11px] font-mono text-slate-400" data-testid={`prod-sku-${item.producto.id}`}>SKU: {item.producto.sku}</div>}
                      <div className="text-xs text-slate-500">{item.producto.color || "—"} · Talla {item.producto.talla || "—"} · {item.producto.cantidad} {item.producto.cantidad === 1 ? "par" : "pares"}</div>
                      <div className="text-[11px] text-slate-400 mt-1">Cliente: {item.cliente_nombre}</div>
                      <div className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-1" data-testid={`fecha-requerida-${item.producto.id}`}>
                        <CalendarClock size={11} className="text-slate-300 shrink-0" /> Requerida: <span className="font-medium text-slate-500">{item.fecha_requerida ? fechaSolo(item.fecha_requerida) : "—"}</span>
                      </div>
                      <div className="text-[11px] mt-0.5 flex items-center gap-1 flex-wrap" data-testid={`fecha-entrega-${item.producto.id}`}>
                        <CalendarClock size={11} className="text-teal-400 shrink-0" />
                        {item.fecha_entrega ? (
                          <span className="text-teal-600">Entrega: <span className="font-semibold">{fechaSolo(item.fecha_entrega)}</span><PeriodoTag date={item.fecha_entrega} fallback /></span>
                        ) : (
                          <span className="text-amber-600">Entrega: pendiente</span>
                        )}
                      </div>
                    </button>
                    {puedeGestionar && (
                      <div className="flex items-center justify-between mt-3 -mx-3 -mb-3 px-3 py-2 bg-slate-50 border-t border-slate-100 rounded-b-xl">
                        <button disabled={idxCol === 0} onClick={() => mover(item, -1)} data-testid={`prev-${item.producto.id}`} className="p-1.5 rounded-md text-slate-400 hover:text-slate-800 hover:bg-slate-200/60 disabled:opacity-30 disabled:cursor-not-allowed" aria-label="Estado anterior"><ChevronLeft size={16} /></button>
                        <span className="text-[10px] uppercase tracking-wide text-slate-300 font-semibold select-none">Estado</span>
                        <button disabled={idxCol === data.estados.length - 1} onClick={() => mover(item, 1)} data-testid={`next-${item.producto.id}`} className="p-1.5 rounded-md text-slate-400 hover:text-slate-800 hover:bg-slate-200/60 disabled:opacity-30 disabled:cursor-not-allowed" aria-label="Siguiente estado"><ChevronRight size={16} /></button>
                      </div>
                    )}
                  </Card>
                ))}
                {cards.length === 0 && <div className="text-center text-xs text-slate-300 py-6">Vacío</div>}
              </div>
            </div>
          );
        })}
        {/* Carril Cancelado (después de Entregado): no admite arrastre; solo llega vía acción "Cancelar". */}
        <div className="w-72 shrink-0" data-testid="col-Cancelado">
          <div className="bg-white rounded-t-xl border border-b-0 border-slate-200 border-t-4 border-t-red-400 px-4 py-3 flex items-center justify-between">
            <span className="font-semibold text-slate-800 text-sm">Cancelado</span>
            <span className="text-xs text-slate-400 tabular-nums" data-testid="col-total-Cancelado">{pares(cancelados)}</span>
          </div>
          <div className="rounded-b-xl border border-t-0 border-slate-200 p-3 space-y-3 min-h-[140px] bg-slate-50">
            {cancelados.map((item) => (
              <Card key={item.producto.id} className="p-3 shadow-sm border-red-200 bg-red-50/40" data-testid={`prod-card-${item.producto.id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[11px] text-slate-400">{item.folio}</span>
                  <span className="text-[10px] font-semibold text-red-600 bg-red-100 px-1.5 py-0.5 rounded">Cancelado</span>
                </div>
                <button onClick={() => navigate(`/muestras/${item.muestra_id}`)} className="text-left w-full" data-testid={`abrir-muestra-${item.producto.id}`}>
                  <div className="font-semibold text-sm text-slate-800 mt-0.5 hover:text-blue-600 transition-colors">{item.producto.nombre}</div>
                  {item.producto.sku && <div className="text-[11px] font-mono text-slate-400">SKU: {item.producto.sku}</div>}
                  <div className="text-xs text-slate-500">{item.producto.color || "—"} · Talla {item.producto.talla || "—"} · {item.producto.cantidad} {item.producto.cantidad === 1 ? "par" : "pares"}</div>
                  <div className="text-[11px] text-slate-400 mt-1">Cliente: {item.cliente_nombre}</div>
                  {item.producto.motivo_cancelacion && <div className="text-[11px] text-red-500 mt-1" data-testid={`motivo-cancel-${item.producto.id}`}>Motivo: {item.producto.motivo_cancelacion}</div>}
                </button>
              </Card>
            ))}
            {cancelados.length === 0 && <div className="text-center text-xs text-slate-300 py-6">Vacío</div>}
          </div>
        </div>
      </div>
      )}
      <Dialog open={!!confirmEntrega} onOpenChange={(o) => { if (!o) setConfirmEntrega(null); }}>
        <DialogContent data-testid="confirmar-entregado-modal">
          <DialogHeader>
            <DialogTitle>¿Marcar como Entregado?</DialogTitle>
            <DialogDescription>Se notificará al solicitante para que confirme la recepción de la muestra.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmEntrega(null)} data-testid="confirmar-entregado-cancelar">Cancelar</Button>
            <Button
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="confirmar-entregado-confirmar"
              onClick={() => { const c = confirmEntrega; setConfirmEntrega(null); if (c) setEstado(c.item, c.estado); }}
            >Confirmar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
